import { useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { data, plural, rotulo } from '../formato';
import { SITUACAO_DO_FORNECEDOR, degrauDe } from '../situacoes';
import {
  Aviso, Carregando, ErroDaApi, Rn, Selo, useAbasQueGrudam, type Tom,
} from '../ui';
import type { Analise, Calibracao, Componente } from './Equipamento';

/**
 * A ficha do equipamento em abas (onda 351, especificação 11 §4, onda 2).
 *
 * Até aqui a ficha empilhava onze blocos, e a pergunta do técnico —
 * «posso usar isto hoje?» — dividia a tela com a posse, as fotos e o
 * histórico da ficha. Agora ela é uma moldura de registro (P-UI-33): o
 * cabeçalho, o «posso usar?» com a ação que resolve, uma faixa de cinco
 * números e as abas com endereço próprio (`?aba=`), para as Pendências
 * levarem direto ao lugar certo e o F5 não perder o lugar.
 *
 * Nada aqui decide aptidão. O motivo vem da `vw_equipamento_impedimento`
 * (o mesmo da lista do parque); a ficha só oferece o caminho.
 */

export type AbaDaFicha =
  | 'geral' | 'calibracao' | 'afericao' | 'manutencao' | 'uso' | 'historico'
  | 'identificacao';

export const ABAS_DA_FICHA: Array<{ aba: AbaDaFicha; nome: string }> = [
  { aba: 'geral', nome: 'Visão geral' },
  { aba: 'calibracao', nome: 'Calibração e análise' },
  { aba: 'afericao', nome: 'Calibrações internas' },
  { aba: 'manutencao', nome: 'Manutenção e fotos' },
  { aba: 'uso', nome: 'Uso e impacto' },
  { aba: 'historico', nome: 'Linha do tempo' },
  { aba: 'identificacao', nome: 'Identificação e posse' },
];

export function abaDoEndereco(valor: string | null): AbaDaFicha {
  return ABAS_DA_FICHA.some((a) => a.aba === valor) ? valor as AbaDaFicha : 'geral';
}

/** As abas — links, não botões: cada uma tem endereço (P-UI-33). */
export function AbasDaFicha({ aba, contagem }: {
  aba: AbaDaFicha; contagem: Partial<Record<AbaDaFicha, number>>;
}) {
  const faixa = useRef<HTMLElement>(null);
  const antesDeTrocar = useAbasQueGrudam(faixa, aba);
  useEffect(() => {
    const nav = faixa.current;
    const sel = nav?.querySelector<HTMLElement>('.aba-os.sel');
    if (!nav || !sel || nav.scrollWidth <= nav.clientWidth) return;
    const r = sel.getBoundingClientRect();
    const rn = nav.getBoundingClientRect();
    if (r.left < rn.left || r.right > rn.right) {
      nav.scrollLeft = Math.max(0, nav.scrollLeft + r.left - rn.left - 16);
    }
  }, [aba]);
  return (
    <nav className="abas-os" aria-label="Abas da ficha do equipamento" ref={faixa}>
      {ABAS_DA_FICHA.map((a) => {
        const n = contagem[a.aba];
        return (
          <Link key={a.aba} to={`?aba=${a.aba}`}
            className={`aba-os${a.aba === aba ? ' sel' : ''}`}
            aria-current={a.aba === aba ? 'page' : undefined}
            onClick={() => antesDeTrocar(a.aba)}>
            {a.nome}
            {n !== undefined && n > 0 && <span className="aba-os-cont">{n}</span>}
          </Link>
        );
      })}
    </nav>
  );
}

// ── «Posso usar?» ───────────────────────────────────────────────────────

export interface Motivo { codigo: string; curto: string; longo: string }

/** O que a ficha oferece para cada motivo. `abrir` é um gesto da ficha. */
export type Acao =
  | { rotulo: string; aba: AbaDaFicha }
  | { rotulo: string; para: string }
  | { rotulo: string; abrir: 'certificado' };

export function acoesDoMotivo(codigo: string, categoria: string,
  pode: (p: string) => boolean): Acao[] {
  switch (codigo) {
    case 'sem_calibracao':
    case 'recalibracao_pendente':
      return [
        ...(pode('equipamento.editar')
          ? [{ rotulo: 'Registrar certificado', abrir: 'certificado' as const }] : []),
        { rotulo: 'Ver a calibração', aba: 'calibracao' },
      ];
    case 'componente_impedido':
      return [{ rotulo: 'Ver os sensores', aba: 'calibracao' }];
    case 'afericao_reprovada':
    case 'sem_valor':
      return [
        ...(pode('equipamento.verificar')
          ? [{ rotulo: 'Calibrar',
            para: `/equipamentos/aferir-massas?categoria=${encodeURIComponent(categoria)}` }]
          : []),
        { rotulo: 'Ver as calibrações internas', aba: 'afericao' },
      ];
    case 'manutencao_aberta':
      return [{ rotulo: 'Ver a manutenção', aba: 'manutencao' }];
    case 'bloqueado':
    case 'fora_de_tolerancia':
      return [
        { rotulo: 'Ver o impacto', aba: 'uso' },
        { rotulo: 'Autorizar uso', aba: 'manutencao' },
      ];
    default:
      return [];
  }
}

export function PossoUsar({
  apto, motivos, impedimentos, ressalvas, categoria, pode, aoAbrirCertificado,
  textoApto, aba,
}: {
  aba: AbaDaFicha;
  apto: boolean; motivos: Motivo[]; impedimentos: string[]; ressalvas: string[];
  categoria: string; pode: (p: string) => boolean;
  aoAbrirCertificado(): void; textoApto: React.ReactNode;
}) {
  /* As ações de todos os motivos, sem repetir — a primeira é a principal. */
  const acoes = useMemo(() => {
    const vistas = new Set<string>();
    const lista: Acao[] = [];
    for (const m of motivos) {
      for (const a of acoesDoMotivo(m.codigo, categoria, pode)) {
        if (vistas.has(a.rotulo)) continue;
        /* O link da aba em que a pessoa já está não leva a lugar nenhum. */
        if ('aba' in a && a.aba === aba) continue;
        vistas.add(a.rotulo);
        lista.push(a);
      }
    }
    return lista;
  }, [motivos, categoria, pode, aba]);

  const frases = motivos.length ? motivos.map((m) => m.longo) : impedimentos;

  return (
    <div className="eq-posso-usar">
      {apto
        ? <Aviso tipo="ok" titulo="Apto para uso">{textoApto}</Aviso>
        : (
          <Aviso tipo="erro" titulo="Não pode entrar em ensaio">
            <p className="eq-motivo-principal">{frases[0]}</p>
            {frases.length > 1 && (
              <ul className="eq-outros-motivos">
                {frases.slice(1).map((s) => <li key={s}>{s}</li>)}
              </ul>
            )}
            {acoes.length > 0 && (
              <div className="eq-acoes">
                {acoes.map((a, i) => {
                  /* Primária só o GESTO que resolve (registrar, aferir), e só
                     o primeiro; ir a uma aba é sempre secundário (P-UI-10). */
                  const classe = i === 0 && !('aba' in a) ? 'botao' : 'botao secundario';
                  if ('abrir' in a) {
                    return (
                      <button key={a.rotulo} type="button" className={classe}
                        onClick={aoAbrirCertificado}>{a.rotulo}</button>
                    );
                  }
                  /* Só a consulta da própria rota, ou um caminho absoluto (327.20). */
                  const destino = 'aba' in a ? `?aba=${a.aba}` : a.para;
                  return (
                    <Link key={a.rotulo} className={classe} to={destino}>{a.rotulo}</Link>
                  );
                })}
              </div>
            )}
          </Aviso>
        )}
      {ressalvas.map((r) => (
        <Aviso key={r} tipo="atencao" titulo="Atenção">{r}</Aviso>
      ))}
      <p className="rodape-regra">
        A trava é do banco, não da tela{' '}
        <Rn codigo="RN-151"
          diz="Ensaio não seleciona equipamento sem calibração externa ou interna válida." />.
      </p>
    </div>
  );
}

// ── O fornecedor do certificado ─────────────────────────────────────────

/** A situação HOJE do fornecedor homologado (0114) que emitiu o certificado. */
export function SeloFornecedor({ c }: { c: Calibracao }) {
  if (!c.fornecedor_id) {
    return <Selo tom="neutro" titulo="Certificado ligado só à empresa, sem fornecedor homologado">Sem fornecedor homologado</Selo>;
  }
  const g = degrauDe(SITUACAO_DO_FORNECEDOR, c.fornecedor_estado);
  return (
    <Selo tom={g.tom}>
      Fornecedor: {g.texto.toLowerCase()}
      {c.fornecedor_estado === 'aprovado' && c.fornecedor_ate && ` até ${data(c.fornecedor_ate)}`}
    </Selo>
  );
}

/**
 * O PDF anexado ou não — o mesmo desenho em toda a ficha (veio da
 * `Equipamento.tsx` na onda 351, para a Visão geral usar o mesmo selo).
 */
export function SeloPdf({ tem }: { tem: boolean }) {
  return (
    <span
      className={`selo-pdf${tem ? '' : ' sem'}`}
      title={tem ? 'PDF anexado' : 'Sem PDF anexado'}
      aria-label={tem ? 'PDF anexado' : 'Sem PDF anexado'}
      role="img"
    >
      {tem ? '▤' : '▢'}
      <span className="txt">PDF</span>
    </span>
  );
}

// ── A faixa de resumo ───────────────────────────────────────────────────

export interface ResumoDaFicha {
  manutencoes_abertas: number;
  usos_no_ano: number;
  proxima_afericao: string | null;
  software: Array<{ tipo: string; nome: string; versao: string; desde: string }>;
}

export function FaixaDeResumo({ resumo, calibracoes, calibravel }: {
  resumo: ResumoDaFicha | null; calibracoes: Calibracao[]; calibravel: boolean;
}) {
  const vigentes = calibracoes.filter((c) => c.vigente && c.valida_ate);
  const proximaCal = [...vigentes].sort((a, b) =>
    String(a.valida_ate).localeCompare(String(b.valida_ate)))[0];
  /* O vencimento mais próximo entre a calibração e a aferição. */
  const candidatos: Array<{ quando: string; de: string }> = [];
  if (proximaCal) {
    candidatos.push({
      quando: String(proximaCal.valida_ate),
      de: proximaCal.da_maquina ? rotulo(proximaCal.grandeza) : proximaCal.de_quem_codigo,
    });
  }
  if (resumo?.proxima_afericao) {
    candidatos.push({ quando: resumo.proxima_afericao, de: 'calibração interna' });
  }
  const proximo = candidatos.sort((a, b) => a.quando.localeCompare(b.quando))[0];

  const ultima = [...vigentes].sort((a, b) =>
    String(b.data_calibracao).localeCompare(String(a.data_calibracao)))[0]
    ?? calibracoes[0];
  const ano = new Date().getFullYear();
  const softwares = resumo?.software ?? [];

  return (
    <dl className="eq-resumo">
      <div className="eq-resumo-item cartao plano">
        <dt>Próximo vencimento</dt>
        <dd>{proximo
          ? <><b>{data(proximo.quando)}</b> <span className="sub">· {proximo.de}</span></>
          : <span className="nada">—</span>}</dd>
      </div>
      <div className="eq-resumo-item cartao plano">
        <dt>Calibra com</dt>
        <dd>{ultima
          ? <><b>{ultima.fornecedor ?? ultima.empresa ?? '—'}</b> <SeloFornecedor c={ultima} /></>
          : <span className="nada">{calibravel ? 'nenhum certificado' : 'não calibra fora'}</span>}</dd>
      </div>
      <div className="eq-resumo-item cartao plano">
        <dt>Software / firmware</dt>
        <dd>{softwares.length
          ? softwares.map((s) => (
            <div key={`${s.tipo}-${s.nome}`}>
              <b>{s.nome || rotulo(s.tipo)} {s.versao}</b>
              <span className="sub"> · desde {data(s.desde)}</span>
            </div>
          ))
          : <span className="nada">não registrado</span>}</dd>
      </div>
      <div className="eq-resumo-item cartao plano">
        <dt>Manutenção</dt>
        <dd>{resumo && resumo.manutencoes_abertas > 0
          ? <Link to="?aba=manutencao" className="eq-resumo-link"><b>{plural(resumo.manutencoes_abertas, 'aberta', 'abertas')}</b></Link>
          : <span className="sub">nenhuma aberta</span>}</dd>
      </div>
      <div className="eq-resumo-item cartao plano">
        <dt>Uso</dt>
        <dd><b>{plural(resumo?.usos_no_ano ?? 0, 'ensaio', 'ensaios')}</b>
          <span className="sub"> em {ano}</span></dd>
      </div>
    </dl>
  );
}

// ── Por grandeza ────────────────────────────────────────────────────────

export interface BlocoDeGrandeza {
  grandeza: string;
  certificados: Calibracao[];
  sensores: Componente[];
  /** Sensor que mede esta grandeza e não tem calibração vigente. */
  descobertos: Componente[];
  vence: string | null;
  apto: boolean;
}

/**
 * Os certificados da máquina e dos sensores, agrupados pela grandeza que
 * o ensaio consulta (onda 94). O vencimento do bloco é o mais próximo
 * entre os que sustentam a grandeza — o `RN-158` em forma de data.
 */
export function blocosDeGrandeza(calibracoes: Calibracao[],
  componentes: Componente[]): BlocoDeGrandeza[] {
  const mapa = new Map<string, BlocoDeGrandeza>();
  const pegar = (g: string) => {
    const chave = g || 'indefinida';
    let b = mapa.get(chave);
    if (!b) {
      b = { grandeza: chave, certificados: [], sensores: [], descobertos: [],
        vence: null, apto: true };
      mapa.set(chave, b);
    }
    return b;
  };
  for (const c of calibracoes) pegar(c.grandeza).certificados.push(c);
  for (const s of componentes) {
    const b = pegar(s.grandeza_medida ?? 'indefinida');
    b.sensores.push(s);
    if (!s.calibracao_vigente) b.descobertos.push(s);
  }
  for (const b of mapa.values()) {
    const vigentes = b.certificados.filter((c) => c.vigente && c.valida_ate);
    b.vence = vigentes.length
      ? vigentes.map((c) => c.valida_ate as string).sort()[0] : null;
    b.apto = b.descobertos.length === 0
      && (vigentes.length > 0 || b.certificados.length === 0);
  }
  /* O que tem pendência primeiro, depois alfabética. */
  return [...mapa.values()].sort((a, b) => {
    if (a.apto !== b.apto) return a.apto ? 1 : -1;
    return a.grandeza.localeCompare(b.grandeza, 'pt-BR');
  });
}

function frase(a: Analise | undefined): string {
  if (!a) return 'sem análise crítica';
  const juizo = `${a.conforme ? 'adequado' : 'não adequado'}${a.restricoes ? ' com restrição' : ''}`;
  return a.aprovado_em
    ? `Análise crítica: ${juizo} · aprovada${a.aprovado_por_nome ? ` por ${a.aprovado_por_nome}` : ''}`
    : `Análise crítica: ${juizo} · aguardando aprovação`;
}

/** «A força está calibrada hoje?» — um cartão por grandeza, na Visão geral. */
export function CartoesPorGrandeza({ blocos, analisePorCal }: {
  blocos: BlocoDeGrandeza[]; analisePorCal: Map<string, Analise>;
}) {
  if (blocos.length === 0) return null;
  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Por grandeza</h2>
        <Link to="?aba=calibracao" className="botao discreto">Ver a calibração</Link>
      </div>
      <div className="eq-grandezas">
        {blocos.map((b) => {
          const vigentes = b.certificados.filter((c) => c.vigente);
          const ultimo = [...b.certificados].sort((x, y) =>
            String(y.valida_ate).localeCompare(String(x.valida_ate)))[0];
          const estado = b.apto && vigentes.length ? 'Em dia'
            : vigentes.length || b.descobertos.length ? 'Com pendência'
              : ultimo ? 'Vencida' : 'Sem certificado';
          const tom: Tom = estado === 'Em dia' ? 'ok' : 'critico';
          return (
            <section key={b.grandeza}
              className={`eq-grandeza-cartao cartao plano${tom === 'ok' ? '' : ' com-pendencia'}`}>
              <div className="linha-topo">
                <h3 className="cresce">{rotulo(b.grandeza)}</h3>
                <Selo tom={tom}>{estado}</Selo>
              </div>
              {vigentes.map((c) => (
                <div key={c.id} className="eq-grandeza-cert">
                  <div>
                    {c.da_maquina ? 'A própria máquina' : <span className="mono">{c.de_quem_codigo}</span>}
                    {' · certificado '}<span className="mono">{c.numero_certificado}</span>
                    {c.valida_ate && <> · até {data(c.valida_ate)}</>}
                  </div>
                  <div className="sub">{frase(analisePorCal.get(c.id))}</div>
                  <div className="eq-grandeza-selos">
                    <SeloPdf tem={!!c.arquivo_id} />
                    <SeloFornecedor c={c} />
                  </div>
                </div>
              ))}
              {vigentes.length === 0 && ultimo && (
                <p className="sub">
                  Certificado <span className="mono">{ultimo.numero_certificado}</span>
                  {ultimo.valida_ate && <> venceu em {data(ultimo.valida_ate)}</>}.
                </p>
              )}
              {vigentes.length === 0 && !ultimo && b.sensores.length === 0 && (
                <p className="nada">Nenhum certificado registrado.</p>
              )}
              {b.descobertos.length > 0 && (
                <p className="sub">
                  {plural(b.descobertos.length, 'sensor sem calibração vigente',
                    'sensores sem calibração vigente')}:{' '}
                  {b.descobertos.map((s) => s.codigo_patrimonial).join(', ')}
                </p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ── A linha do tempo ────────────────────────────────────────────────────

export interface Evento {
  dia: string; instante: string | null; tipo: string; titulo: string;
  detalhe: string | null; quem: string | null; aba: AbaDaFicha;
}

const NOME_DO_TIPO: Record<string, string> = {
  calibracao: 'Calibração', vencimento: 'Vencimento', analise: 'Análise crítica',
  afericao: 'Calibração interna', manutencao: 'Manutenção', autorizacao: 'Autorização de uso',
  posse: 'Posse', software: 'Software e firmware', baixa: 'Baixa',
  cadastro: 'Cadastro', aquisicao: 'Aquisição',
};

export function LinhaDoTempo({ eventos, limite, comFiltro, erro }: {
  eventos: Evento[] | null; limite?: number; comFiltro?: boolean; erro?: unknown;
}) {
  /* O recorte mora no endereço (P-LST-05): F5 e link mandado a um colega
     abrem a mesma linha do tempo filtrada. */
  const [busca, setBusca] = useSearchParams();
  const tipoNoEndereco = comFiltro ? busca.get('tipo') : null;
  const setTipo = (t: string | null) => {
    const b = new URLSearchParams(busca);
    if (t) b.set('tipo', t); else b.delete('tipo');
    setBusca(b, { replace: true });
  };
  if (erro) return <ErroDaApi erro={erro} />;
  if (eventos === null) return <Carregando texto="Carregando a linha do tempo" />;
  const tipo = tipoNoEndereco && eventos.some((e) => e.tipo === tipoNoEndereco)
    ? tipoNoEndereco : null;
  const tipos = [...new Set(eventos.map((e) => e.tipo))];
  const filtrados = tipo ? eventos.filter((e) => e.tipo === tipo) : eventos;
  const mostrados = limite ? filtrados.slice(0, limite) : filtrados;
  return (
    <>
      {comFiltro && tipos.length > 1 && (
        <div className="eq-filtro-tempo" role="group" aria-label="Filtrar a linha do tempo">
          <button type="button" className="chip" aria-pressed={tipo === null}
            onClick={() => setTipo(null)}>Tudo · {eventos.length}</button>
          {tipos.map((t) => (
            <button key={t} type="button" className="chip" aria-pressed={tipo === t}
              onClick={() => setTipo(tipo === t ? null : t)}>
              {NOME_DO_TIPO[t] ?? rotulo(t)} · {eventos.filter((e) => e.tipo === t).length}
            </button>
          ))}
        </div>
      )}
      {mostrados.length === 0
        ? <p className="nada">Nada registrado ainda.</p>
        : (
          <ol className="eq-linha-do-tempo">
            {mostrados.map((e, i) => (
              <li key={`${e.dia}-${e.tipo}-${i}`} className={`eq-evento ${e.tipo}`}>
                <span className="eq-evento-dia">{data(e.dia)}</span>
                <div className="eq-evento-texto">
                  <span className="eq-evento-tipo">{NOME_DO_TIPO[e.tipo] ?? rotulo(e.tipo)}</span>
                  <b>{e.titulo}</b>
                  {(e.detalhe || e.quem) && (
                    <div className="sub">
                      {[e.detalhe, e.quem].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
    </>
  );
}
